import React, { useState, useRef, useCallback, useEffect } from "react";

/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (16S V1-V9 pipeline)
───────────────────────────────────────── */
const DEFAULT_METHODOLOGY = `DNA Extraction, Library Preparation and Sequencing:

The rhizospheres soil was used for DNA extractions using FastDNA™ Spin Kit for Soil (MP Biomedicals, USA) following the protocol provided by the manufacturer. The 16S rRNA gene amplicon libraries were prepared using the Oxford Nanopore Technologies (ONT) 16S Barcoding Kit 24 V14 (SQK-16S024) according to the manufacturer's protocol. Briefly, 10–50 ng of purified genomic DNA from each sample was used as input for PCR amplification of the full-length 16S rRNA gene using ONT's pre-mixed 16S primers (covering the V1–V9 regions). The PCR amplification was carried out using LongAmp™ Taq 2X Master Mix (New England Biolabs) in a final volume of 50 µL per reaction, under the following thermal cycling conditions: initial denaturation at 95°C for 3 min; followed by 35 cycles of 95°C for 20 sec, 55°C for 30 sec, and 65°C for 2 min; and a final extension at 65°C for 5 min.

Barcoding of amplicons was performed using ONT's native barcode primers provided in the kit, and equimolar amounts of barcoded amplicons were pooled. The pooled amplicons were purified using AMPure XP beads (Beckman Coulter) at a 1:1 bead-to-sample ratio and quantified using Qubit dsDNA HS Assay Kit (Thermo Fisher Scientific). The final pooled library was prepared by adding the sequencing adapters using the ONT ligation-based chemistry. Sequencing was performed on PromethION 2 Solo platform (Oxford Nanopore Technologies, Oxford, UK) using R10.4.1 flow cells (FLO-PRO114) on MinKNOW (version 24.06.14, ONT).

Bioinformatic Analyses

The reads obtained after sequencing were first QC checked with the tool Nanoplot v1.43.0. The reads were then trimmed to remove adapters with the tool Porechop v0.2.4. The tool Krakenv2 available with the Epi2Me platform was used to assign taxonomy and calculate the relative abundances based on the NCBI 16S RefSeq database. The obtained results were visualized using the R package ggplot2 v3.5.1. The alpha diversity values were calculated using the R package Vegan v2.6-6 and visualized using ggplot2. The Krona chart of the microbiome distribution were generated with the help of Krona Tools v2.8.1. Differential microbial features between groups were identified using the lefseR tool, applying Kruskal–Wallis and Wilcoxon tests with LDA for effect size estimation. Functional profiles of the microbial communities were predicted using the Tax4Fun tool v1.1.5.`;

function parseTxtFile(text) {
  return text
    .trim().split(/\r?\n/).filter(Boolean)
    .map(l => l.split(/\t/).map(c => c.trim()))
    .filter(cols => cols.length >= 2)
    .filter(([s]) => !["sample","sample-id","sample_id"].includes(s.toLowerCase()))
    .map(([sample, condition]) => ({ sample, condition }));
}

/* ─────────────────────────────────────────
   DYNAMIC THEME STYLES (WGS UI System)
───────────────────────────────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700;800&display=swap');
.v1v9-page *, .v1v9-page *::before, .v1v9-page *::after { box-sizing: border-box; }
.v1v9-page { font-family:'Barlow',sans-serif; background:#f4f6f8; min-height:100vh; padding:0 0 80px; }
.v1v9-page .v1v9-content { max-width:960px; margin:0 auto; padding:28px 16px; display:flex; flex-direction:column; gap:20px; }
.v1v9-page .section-card { background:#fff; border-radius:12px; border:1px solid #e5e7eb; padding:28px 32px; box-shadow:0 1px 4px rgba(0,0,0,0.06); }
.v1v9-page .section-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:24px; }
.v1v9-page .section-header-text h3 { margin:0 0 3px; font-size:19px; font-weight:800; color:#111827; }
.v1v9-page .section-header-text p { margin:0; font-size:13px; color:#6b7280; }
.v1v9-page .divider { height:1px; background:#f3f4f6; margin:20px 0; }
.v1v9-page .sub-label { font-size:13px; font-weight:800; text-transform:uppercase; letter-spacing:.8px; color:#059669; margin-bottom:12px; padding-bottom:6px; border-bottom:1.5px solid #d1fae5; }
.v1v9-page .g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.v1v9-page .g3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
@media(max-width:768px){ .v1v9-page .g2, .v1v9-page .g3 { grid-template-columns:1fr; } }
.v1v9-page .field { display:flex; flex-direction:column; gap:5px; }
.v1v9-page .field > label { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:#374151; }
.v1v9-page .field > label .req { color:#dc2626; margin-left:2px; }
.v1v9-page .field input { padding:10px 14px; border:1.5px solid #e5e7eb; border-radius:8px; font-size:14px; font-family:'Barlow',sans-serif; color:#111827; background:#fff; outline:none; width:100%; transition:border-color .18s, box-shadow .18s; }
.v1v9-page .field input:focus { border-color:#4dd9ac; box-shadow:0 0 0 3px rgba(77,217,172,.2); }
.v1v9-page .field input::placeholder { color:#c9cdd4; }

.v1v9-page .drop-area { border:2px dashed #a7f3d0; border-radius:10px; padding:20px 16px; text-align:center; cursor:pointer; background:#f0fdf4; position:relative; transition:all .18s; }
.v1v9-page .drop-area:hover, .v1v9-page .drop-area.drag { border-color:#4dd9ac; background:#d1fae5; }
.v1v9-page .drop-area input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.v1v9-page .drop-main { font-size:13px; font-weight:600; color:#047857; margin-top:6px; }
.v1v9-page .drop-hint { font-size:11px; color:#9ca3af; margin-top:2px; }

.v1v9-page .chip-list { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.v1v9-page .chip { display:inline-flex; align-items:center; gap:5px; background:#d1fae5; color:#047857; border:1px solid #a7f3d0; border-radius:20px; padding:3px 10px 3px 8px; font-size:11px; font-weight:500; max-width:260px; }
.v1v9-page .chip span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.v1v9-page .chip button { background:none; border:none; cursor:pointer; color:#047857; font-size:13px; line-height:1; padding:0; margin-left:2px; }

.v1v9-page .info-banner { display:flex; align-items:flex-start; gap:10px; background:#f0fdf4; border:1px solid #a7f3d0; border-radius:8px; padding:12px 14px; margin-bottom:16px; }
.v1v9-page .info-banner-icon { font-size:15px; flex-shrink:0; margin-top:1px; }
.v1v9-page .info-banner-text { font-size:12px; color:#047857; line-height:1.6; }
.v1v9-page .info-banner-text strong { font-weight:700; }

.v1v9-page .counter { display:inline-flex; align-items:center; border:1.5px solid #e5e7eb; border-radius:8px; overflow:hidden; }
.v1v9-page .counter button { width:32px; height:36px; background:#f9fafb; border:none; cursor:pointer; font-size:16px; color:#374151; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.v1v9-page .counter button:hover { background:#d1fae5; color:#047857; }
.v1v9-page .counter span { width:44px; text-align:center; font-size:14px; font-weight:600; color:#111827; border-left:1px solid #e5e7eb; border-right:1px solid #e5e7eb; padding:8px 0; font-variant-numeric:tabular-nums; }

.v1v9-page .comp-num-badge { width:26px; height:26px; background:#4dd9ac; color:#0a1628; border-radius:50%; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.v1v9-page .comp-title-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }

.v1v9-page .method-area { width:100%; min-height:240px; resize:vertical; border:1.5px solid #e5e7eb; border-radius:10px; padding:14px 16px; font-size:14px; line-height:1.85; color:#111827; font-family:'Barlow',sans-serif; background:#fafbfd; outline:none; box-sizing:border-box; }
.v1v9-page .method-area:focus { border-color:#4dd9ac; box-shadow:0 0 0 3px rgba(77,217,172,.2); }
.v1v9-page .method-meta { display:flex; align-items:center; justify-content:space-between; margin-top:7px; padding:0 2px; }
.v1v9-page .method-word-count { font-size:11px; color:#6b7280; font-variant-numeric:tabular-nums; }
.v1v9-page .method-clear { background:none; border:none; cursor:pointer; font-size:11px; color:#dc2626; font-weight:700; padding:2px 6px; border-radius:5px; }
.v1v9-page .method-clear:hover { background:#fee2e2; }

.submit-bar { position:fixed; bottom:0; left:0; right:0; background:#fff; border-top:1px solid #e5e7eb; padding:14px 32px; display:flex; align-items:center; justify-content:flex-end; gap:12px; box-shadow:0 -2px 12px rgba(0,0,0,0.07); z-index:100; }
.btn-submit { background:linear-gradient(135deg,#4dd9ac,#059669); color:#0a1628; border:none; border-radius:8px; padding:12px 36px; font-size:14px; font-weight:600; font-family:'Barlow',sans-serif; cursor:pointer; display:flex; align-items:center; gap:8px; transition:background .18s, transform .15s; box-shadow:0 4px 14px rgba(77,217,172,.35); }
.btn-submit:hover:not(:disabled) { transform:translateY(-1px); }
.btn-submit:disabled { opacity:0.7; cursor:not-allowed; }
.submit-error { font-size:12px; font-weight:600; color:#dc2626; background:#fee2e2; border:1px solid #fca5a5; border-radius:7px; padding:7px 12px; }
.submit-success { font-size:12px; font-weight:600; color:#15803d; background:#f0fdf4; border:1px solid #86efac; border-radius:7px; padding:7px 12px; }

@keyframes v1v9-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
`;

function injectStyle() {
  let s = document.getElementById("v1v9-style");
  if (!s) {
    s = document.createElement("style");
    s.id = "v1v9-style";
    document.head.appendChild(s);
  }
  s.textContent = STYLE;
}

/* ─────────────────────────────────────────
   REUSABLE DROPZONE COMPONENT
───────────────────────────────────────── */
function DropArea({ accept, icon = "📁", mainLabel, subLabel, file, onFile, isMulti = false, files = [] }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) {
      if (isMulti) onFile(Array.from(e.dataTransfer.files));
      else onFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files?.length) {
      if (isMulti) onFile(Array.from(e.target.files));
      else onFile(e.target.files[0]);
    }
  };

  return (
    <div
      className={`drop-area ${drag ? "drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept={accept} multiple={isMulti} style={{ display: "none" }} onChange={handleChange} />
      <div style={{ fontSize: 24, lineHeight: 1 }}>{file || (isMulti && files.length > 0) ? "✅" : icon}</div>
      {file ? (
        <>
          <div className="drop-main">{file.name}</div>
          <div className="drop-hint">{(file.size / 1024).toFixed(1)} KB</div>
        </>
      ) : (
        <>
          <div className="drop-main">{mainLabel}</div>
          <div className="drop-hint">{subLabel}</div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function V1V9Upload({ setReportUrls, setActiveTab }) {
  useEffect(() => {
    injectStyle();
    return () => {
      const s = document.getElementById("v1v9-style");
      if (s) s.remove();
    };
  }, []);

  const [ci, setCi] = useState({ reportTitle:"", clientName:"", projectId:"", institution:"", contact:"", salesperson:"" });
  const [sampleRows, setSampleRows] = useState([]);
  const [txtFile, setTxtFile] = useState(null);
  const [numComp, setNumComp] = useState(1);
  const [compTitles, setCompTitles] = useState([""]);
  const [qcZip, setQcZip] = useState(null);
  const [compZip, setCompZip] = useState(null);
  const [resourcesZip, setResourcesZip] = useState(null);
  const [methodologyText, setMethodologyText] = useState(DEFAULT_METHODOLOGY);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleCi = e => setCi(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleTxtFile = useCallback(f => {
    setTxtFile(f);
    const r = new FileReader();
    r.onload = ev => setSampleRows(parseTxtFile(ev.target.result));
    r.readAsText(f);
  }, []);

  const handleNumComp = val => {
    const n = Math.max(1, Math.min(20, Number(val) || 1));
    setNumComp(n);
    setCompTitles(prev => {
      const a = [...prev];
      while (a.length < n) a.push("");
      return a.slice(0, n);
    });
  };

  const handleCompTitle = (i, v) => setCompTitles(p => p.map((x, idx) => idx === i ? v : x));

  const handleUpload = async () => {
    setError(""); setSuccess(false);

    const fd = new FormData();
    if (qcZip) fd.append("qcZip", qcZip);
    if (compZip) fd.append("compZip", compZip);
    if (resourcesZip) fd.append("resourcesZip", resourcesZip);
    if (txtFile) fd.append("sampleFile", txtFile);
    fd.append("clientInfo",      JSON.stringify(ci));
    fd.append("sampleRows",      JSON.stringify(sampleRows));
    fd.append("compTitles",      JSON.stringify(compTitles.filter(t => t && t.trim())));
    fd.append("logoBase64",      "");
    fd.append("methodologyText", methodologyText);

    try {
      setLoading(true);
      const res = await fetch("/api/16sv1-v9/generate-report", { method:"POST", body:fd });
      
      const contentType = res.headers.get("content-type");
      let data = null;
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(`Server returned unexpected response (${res.status}): ${text.slice(0, 120)}`);
      }

      if (!res.ok || !data.success) {
        throw new Error(data.error || data.detail || "Report generation failed");
      }

      const downloadFilename = data.filename || `${(ci.clientName || "client").replace(/[^a-z0-9]/gi, "_")}_${(ci.projectId || "16S_report").replace(/[^a-z0-9]/gi, "_")}.html`;
      const fullReportUrl = data.reportUrl || data.htmlUrl;

      // Automatically trigger HTML report file download
      const dlLink = document.createElement("a");
      dlLink.href = data.downloadUrl || `/api/16sv1-v9/download-report?file=${encodeURIComponent(downloadFilename)}`;
      document.body.appendChild(dlLink);
      dlLink.click();
      document.body.removeChild(dlLink);


      if (setReportUrls) {
        setReportUrls({
          html:         fullReportUrl,
          qcExcel:      data.qcExcelUrl || "",
          mappingExcel: data.mappingExcelUrl || "",
          variantExcel: data.variantExcelUrl || "",
        });
      }
      setSuccess(true);
      if (setActiveTab) setActiveTab("report");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const wordCount = methodologyText.trim() ? methodologyText.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="v1v9-page">
      <div className="v1v9-content">

        {/* ══ 01 CLIENT INFO ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Client &amp; Project Information</h3>
              <p>Metadata that appears in report header</p>
            </div>
          </div>
          <div className="sub-label">General Metadata</div>
          <div className="g2">
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Report Title <span className="req">*</span></label>
              <input name="reportTitle" value={ci.reportTitle} onChange={handleCi} placeholder="e.g. 16S rRNA Amplicon Sequencing Report" />
            </div>
            <div className="field">
              <label>Client / Customer Name <span className="req">*</span></label>
              <input name="clientName" value={ci.clientName} onChange={handleCi} placeholder="e.g. Dr. Jane Doe" />
            </div>
            <div className="field">
              <label>Project ID / Code <span className="req">*</span></label>
              <input name="projectId" value={ci.projectId} onChange={handleCi} placeholder="e.g. OPL_16S_2024_01" />
            </div>
            <div className="field">
              <label>Institution / Organization</label>
              <input name="institution" value={ci.institution} onChange={handleCi} placeholder="e.g. ICAR-Indian Agricultural Research Institute" />
            </div>
            <div className="field">
              <label>Contact Email / Phone <span className="req">*</span></label>
              <input name="contact" value={ci.contact} onChange={handleCi} placeholder="e.g. client@iari.res.in" />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Salesperson / Executive</label>
              <input name="salesperson" value={ci.salesperson} onChange={handleCi} placeholder="e.g. Sales Team / Technical Lead" />
            </div>
          </div>
        </div>

        {/* ══ 02 UPLOAD DATA & COMPARISONS ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Upload Data &amp; Define Comparisons</h3>
              <p>ZIP archives containing HTML charts, images &amp; taxonomy output</p>
            </div>
          </div>

          <div className="g2">
            <div>
              <div className="sub-label">QC &amp; Abundance Archive (ZIP)</div>
              <DropArea accept=".zip" mainLabel="Upload QC ZIP *" subLabel="Contains Nanoplot, Taxonomy & Krona HTML files" icon="📦" file={qcZip} onFile={setQcZip} />

              <div className="sub-label" style={{ marginTop: 20 }}>Comparisons Archive (ZIP)</div>
              <DropArea accept=".zip" mainLabel="Upload Comparisons ZIP *" subLabel="Contains comp_1, comp_2 ... subfolders" icon="📊" file={compZip} onFile={setCompZip} />

              <div className="sub-label" style={{ marginTop: 20 }}>Sample-Condition Map (.txt / TSV)</div>
              <DropArea accept=".txt,.tsv" mainLabel="Upload Sample Info (Optional)" subLabel="Tab-separated: Sample <tab> Condition" icon="📄" file={txtFile} onFile={handleTxtFile} />

              {sampleRows.length > 0 && (
                <div className="info-banner" style={{ marginTop: 12 }}>
                  <div className="info-banner-icon">📋</div>
                  <div className="info-banner-text">Parsed <strong>{sampleRows.length}</strong> samples from sample info file.</div>
                </div>
              )}
            </div>

            <div>
              <div className="sub-label">Comparison Groups</div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#374151", display: "block", marginBottom: 6 }}>Number of Comparisons</label>
                <div className="counter">
                  <button type="button" onClick={() => handleNumComp(numComp - 1)}>−</button>
                  <span>{numComp}</span>
                  <button type="button" onClick={() => handleNumComp(numComp + 1)}>+</button>
                </div>
              </div>

              <div className="sub-label">Comparison Display Titles</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {compTitles.map((t, i) => (
                  <div key={i} className="comp-title-row">
                    <div className="comp-num-badge">{i + 1}</div>
                    <input
                      className="field"
                      style={{ flex: 1, padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Barlow', sans-serif", outline: "none", color: "#111827", background: "#fff" }}
                      placeholder={`e.g. Group A vs Group B (comp_${i + 1})`}
                      value={t}
                      onChange={e => handleCompTitle(i, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══ DATA FILES & DELIVERABLES (RESOURCES TAB) ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Data Files &amp; Deliverables (Resources Tab)</h3>
              <p>Upload a single ZIP containing only the important files you want listed in the Resources → Data Files &amp; Deliverables section of the report</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">💡</div>
            <div className="info-banner-text">
              <strong>Tip:</strong> Put only final deliverables inside this ZIP (e.g. OTU / ASV tables, Krona charts, taxonomy matrices, alpha/beta diversity plots, BIOM files). These files will appear directly in the report's <strong>Resources</strong> tab with individual download buttons and a <strong>Download All (.ZIP)</strong> option.
            </div>
          </div>
          <DropArea
            accept=".zip"
            mainLabel="Upload Resources / Deliverables ZIP"
            subLabel="One ZIP file – contents will be extracted and listed in the Resources tab"
            icon="📦"
            file={resourcesZip}
            onFile={setResourcesZip}
          />
        </div>

        {/* ══ 03 METHODOLOGY ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Materials &amp; Methods</h3>
              <p>Editable methodology text rendered in Section 2 of the final report</p>
            </div>
          </div>
          <div className="sub-label">Methodology Text</div>
          <textarea
            className="method-area"
            value={methodologyText}
            onChange={e => setMethodologyText(e.target.value)}
            rows={10}
          />
          <div className="method-meta">
            <span className="method-word-count">{wordCount} words</span>
            <button className="method-clear" onClick={() => setMethodologyText(DEFAULT_METHODOLOGY)}>Reset to Default</button>
          </div>
        </div>

      </div>

      {/* ══ FIXED BOTTOM ACTION BAR ══ */}
      <div className="submit-bar">
        {error && <div className="submit-error">⚠️ {error}</div>}
        {success && <div className="submit-success">✅ Report generated successfully!</div>}
        <button className="btn-submit" disabled={loading} onClick={handleUpload}>
          {loading ? "Generating 16S Report..." : " Upload & Generate report"}
        </button>
      </div>
    </div>
  );
}